export interface DocumentCancellationSummary {
  type: 'fattura' | 'ddt' | 'nota_credito' | 'nota_debito' | 'autofattura';
  number: string | null;
  date: string | null;
  supplierName: string | null;
}

export interface DocumentCancellationConfirmation {
  header: string;
  subHeader: string;
  message: string;
  confirmText: string;
}

export type PendingDocumentStage = 'ready' | 'failed';

const DOCUMENT_COPY: Record<DocumentCancellationSummary['type'], {
  label: string;
  demonstrative: string;
  article: string;
}> = {
  fattura: { label: 'fattura', demonstrative: 'questa fattura', article: 'la fattura' },
  ddt: { label: 'DDT', demonstrative: 'questo DDT', article: 'il DDT' },
  nota_credito: { label: 'nota di credito', demonstrative: 'questa nota di credito', article: 'la nota di credito' },
  nota_debito: { label: 'nota di debito', demonstrative: 'questa nota di debito', article: 'la nota di debito' },
  autofattura: { label: 'autofattura', demonstrative: 'questa autofattura', article: 'l’autofattura' },
};

function formatDocumentDate(value: string | null): string {
  if (!value) return 'data non disponibile';
  const day = value.slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return value;
  return `${match[3]}/${match[2]}/${match[1]}`;
}

/**
 * T-614: copy esplicita e riconoscibile prima del rollback di un documento.
 * Il testo resta puro (niente HTML da dati OCR/fornitore) e ricorda che il
 * backend conserva l'audit attraverso movimenti compensativi append-only.
 */
export function buildDocumentCancellationConfirmation(
  summary: DocumentCancellationSummary,
): DocumentCancellationConfirmation {
  const copy = DOCUMENT_COPY[summary.type];
  const rawNumber = summary.number?.trim();
  const number = rawNumber && rawNumber !== '—' ? rawNumber : 'numero non disponibile';
  const supplier = summary.supplierName?.trim() || 'fornitore non disponibile';

  return {
    header: `Conferma annullamento ${copy.label}`,
    subHeader: `${copy.label} n. ${number} · ${formatDocumentDate(summary.date)} · ${supplier}`,
    message: `Vuoi veramente annullare ${copy.demonstrative}? Il documento, la scansione e i lotti caricati non saranno più utilizzabili né ripristinabili dall'interfaccia. La giacenza verrà compensata e lo storico resterà tracciato.`,
    confirmText: `Sì, annulla ${copy.article}`,
  };
}

/**
 * T-639: conferma della X nella lista Documenti. Qui il documento non è stato
 * ancora importato, quindi la copy non deve parlare di compensazioni stock.
 */
export function buildPendingDocumentDismissConfirmation(
  summary: DocumentCancellationSummary,
  stage: PendingDocumentStage,
): DocumentCancellationConfirmation {
  const copy = DOCUMENT_COPY[summary.type];
  const rawNumber = summary.number?.trim();
  const number = rawNumber && rawNumber !== '—' ? rawNumber : 'numero non disponibile';
  const supplier = summary.supplierName?.trim() || 'fornitore non disponibile';
  const stageLabel = stage === 'ready' ? 'documento pronto' : 'elaborazione fallita';

  return {
    header: `Conferma rimozione ${stageLabel}`,
    subHeader: `${copy.label} n. ${number} · ${formatDocumentDate(summary.date)} · ${supplier}`,
    message: `Vuoi rimuovere ${copy.demonstrative} dalla lavorazione? Non è ancora stato caricato in magazzino: il magazzino resterà invariato, ma questa importazione non sarà più riprendibile dall’interfaccia.`,
    confirmText: 'Sì, rimuovi',
  };
}
